import {
  Box,
  FormControlLabel,
  Grid2 as Grid,
  Switch,
  TextField,
} from "@mui/material";
import { forwardRef, useImperativeHandle } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { SelectField } from "src/shared/components";
import { IAddressShort } from "src/shared/types/address.types";
import { ECountries } from "src/shared/types/common.types";
import { messages } from "src/shared/utils/hookFormRules";
import { postcodeRule } from "src/shared/utils/hookFormRules/rules";

interface IAddressFormProps {
  address: IAddressShort;
  handleSave: (vals: IAddressShort) => void;
  registrationCountryId: number;
}
export const AddressForm = forwardRef(
  ({ address, handleSave, registrationCountryId }: IAddressFormProps, ref) => {
    const { data: dropdowns } = useGetPartnerDropdownsQuery();
    const { country: countriesOptions } = dropdowns || {};
    const methods = useForm<IAddressShort>({
      defaultValues: address,
      shouldFocusError: true,
    });
    const { control } = methods;
    const { handleSubmit } = methods;
    useImperativeHandle(
      ref,
      () => {
        return {
          submit() {
            return handleSubmit(handleSave)();
          },
        };
      },
      [handleSave, handleSubmit],
    );

    const isRu = registrationCountryId === ECountries.RU;
    const isBy = registrationCountryId === ECountries.BY;

    return (
      <Box component="form" onSubmit={handleSubmit(handleSave)}>
        <Box
          sx={{
            p: 4,
          }}
        >
          <Grid container spacing={4}>
            {address.type === "legal" && (
              <Grid size={12}>
                <Controller
                  control={control}
                  name="isPostAddressTheSame"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={!!field.value} />}
                      label="Почтовый адрес совпадает с юридическим"
                    />
                  )}
                />
              </Grid>
            )}
            {address.type !== "postal_additional" && (
              <Grid size={12}>
                <Controller
                  control={control}
                  name="country_id"
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <SelectField
                      inputRef={ref}
                      fullWidth
                      {...field}
                      label="Страна"
                      error={!!error}
                      helperText={error?.message}
                      options={countriesOptions}
                    />
                  )}
                  rules={{
                    required: messages.required,
                  }}
                />
              </Grid>
            )}
            <Grid size={12}>
              <Controller
                control={control}
                name="region"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label="Область"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="area"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label="Район"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="city"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label="Город"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="address"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label="Адрес"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                rules={{
                  required: isRu || isBy ? messages.required : undefined,
                }}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                control={control}
                name="postcode"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label="Индекс"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                rules={postcodeRule}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  },
);

AddressForm.displayName = "AddressForm";
